import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { NodeSelection } from 'prosemirror-state';
import ResizableIframeView from './resizableIframeView';

export interface IframeOptions {
  HTMLAttributes: Record<string, string>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    iframe: {
      setIframe: (options: {
        src: string;
        title: string;
        width?: number;
        height?: number;
        align?: 'left' | 'center' | 'right';
      }) => ReturnType;
      setIframeAlignment: (align: 'left' | 'center' | 'right') => ReturnType;
    };
  }
}

export const CustomizedIframeElement = Node.create<IframeOptions>({
  name: 'iframe',
  group: 'block',
  atom: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      ...this.parent?.(),

      src: {
        parseHTML: (element) => element.getAttribute('src'),
      },
      title: {
        parseHTML: (element) => element.getAttribute('title'),
      },
      id: {
        parseHTML: (element) => element.getAttribute('id'),
      },
      scrolling: {
        parseHTML: (element) => element.getAttribute('scrolling'),
      },
      frameborder: {
        parseHTML: (element) => element.getAttribute('frameborder'),
      },
      width: {
        parseHTML: (element) => element.getAttribute('width'),
      },
      height: {
        parseHTML: (element) => element.getAttribute('height'),
      },
      allow: {
        parseHTML: (element) => element.getAttribute('allow'),
      },
      contenteditable: { default: false },
      align: {
        default: 'left',
        parseHTML: (el) => el.getAttribute('data-align') || 'left',
        renderHTML: ({ align }) => {
          let style = 'display: block;';
          if (align === 'center') style += 'margin: 0 auto;';
          else if (align === 'right') style += 'margin: 0 0 0 auto;';
          else style += 'margin: 0 auto 0 0;';
          return { 'data-align': align, style };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: 'iframe' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['iframe', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableIframeView);
  },

  addCommands() {
    return {
      setIframe: (options) => ({ state, dispatch }) => {
        const { schema, tr } = state;
        const iframeNode = schema.nodes['iframe'].create(options);
      
        // Capture insertion position
        const insertPos = tr.selection.from;
      
        // Insert iframe node
        tr.replaceSelectionWith(iframeNode);
      
        // Get the inserted node
        const insertedNode = tr.doc.nodeAt(insertPos);
      
        // Set NodeSelection only if the inserted node is valid and of the correct type
        if (insertedNode && insertedNode.type.name === 'iframe') {
          tr.setSelection(NodeSelection.create(tr.doc, insertPos) as any);
        }
      
        if (dispatch) {
          dispatch(tr.scrollIntoView());
        }
      
        return true;
      },      
      setIframeAlignment: (align) => ({ commands }) => {
        return commands.updateAttributes('iframe', { align });
      },
    };
  },
});
